import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import telehealthBackground from '../assets/telehealth.webp';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
 
const benefits = [
  {
    title: 'Convenience',
    description:
      'Attend appointments from the comfort of your home or another private space without the stress of commuting.',
  },
  {
    title: 'Improved Access to Care',
    description:
      'Telehealth can make care easier to access for patients with busy schedules, transportation barriers, or limited mobility.',
  },
  {
    title: 'Continuity of Care',
    description:
      'Virtual visits help you stay consistent with your treatment plan, even when life gets busy or unexpected changes happen.',
  },
  {
    title: 'Privacy and Comfort',
    description:
      'Many patients feel more at ease connecting with their provider from a familiar and comfortable environment.',
  },
  {
    title: 'Reduced Stigma',
    description:
      'Telehealth offers a discreet way to prioritize your mental health without needing to visit a physical office.',
  },
  {
    title: 'Flexible Follow-Ups',
    description:
      'Virtual appointments can be helpful for follow-ups, medication management, and ongoing mental health support.',
  },
];

const Telehealth = () => (
  <>
    <Helmet>
      <title>Telehealth Psychiatry | Aspire Behavioral Health</title>
      <meta
        name="description"
        content="Aspire Behavioral Health offers secure and convenient telehealth appointments for psychiatry, medication management, follow-up visits, and ongoing mental health support."
      />
    </Helmet>

    {/* Hero Section */}
    <Box
      sx={{
        position: 'relative',

        minHeight: {
          xs: 520,
          sm: 580,
          md: 650,
          lg: 620,
        },

        py: {
          xs: 8,
          sm: 10,
          md: 12,
          lg: 10,
        },

        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        color: 'white',
      }}
    >
      {/* Background Image */}
      <Box
        component="img"
        src={telehealthBackground}
        alt="Telehealth care at Aspire Behavioral Health"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: -2,
        }}
      />

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: -1,
        }}
      />

      {/* Hero Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          px: 3,
          maxWidth: '850px',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: '2.2rem', sm: '3.5rem' },
            lineHeight: 1.2,
          }}
        >
          Mental Health Care From Wherever You Are
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 300,
            lineHeight: 1.8,
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.2rem' },
          }}
        >
          Secure, convenient telehealth appointments designed to make care more
          accessible, comfortable, and flexible.
        </Typography>

        {/* Hero Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            href="tel:2108669850"
            sx={{
              backgroundColor: '#e3f3c5',
              color: 'black',
              fontWeight: 600,
              px: 4,
              py: 1.3,
              borderRadius: '30px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#d3eab0',
              },
            }}
          >
            Call 210-866-9850
          </Button>

          <Button
                        component="a"
                        href={zocdocLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        sx={{
                          borderColor: 'white',
                          color: 'white !important',
                          fontWeight: 600,
                          px: 4,
                          py: 1.3,
                          borderRadius: '30px',
                          textTransform: 'none',
                          '&:visited': {
                            color: 'white',
                          },
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                          },
                        }}
                      >
                        Schedule an Appointment
                      </Button>
        </Box>
      </Box>
    </Box>

    {/* Main Content */}
    <Box sx={{ backgroundColor: 'white', py: 8, px: 2 }}>
      {/* Approach Section */}
      <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Telehealth Made Simple
        </Typography>

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.9,
            color: 'text.secondary',
            fontSize: { xs: '1rem', sm: '1.05rem' },
          }}
        >
          At Aspire Behavioral Health, we offer TelePsychiatry and TelePsychology
          services to help make mental health care more accessible. Virtual
          appointments allow you to meet with your provider from the comfort and
          privacy of your home.
          <br />
          <br />
          Telehealth can be especially helpful for patients with busy schedules,
          transportation challenges, mobility concerns, or those who live farther
          from the office. These visits are designed to be convenient, secure,
          and supportive while still providing quality care.
          <br />
          <br />
          We accept most major insurance plans. To schedule an appointment, call{' '}
          <a href="tel:2108669850">210-866-9850</a>.
        </Typography>
      </Box>

      <Divider
        sx={{
          width: { xs: '80%', sm: '60%', md: '40%' },
          mx: 'auto',
          mb: 7,
          borderColor: '#d9d9d9',
        }}
      />

      {/* Benefits Section */}
      <Box
        sx={{
          backgroundColor: '#e3f3c5',
          color: 'black',
          maxWidth: '1200px',
          mx: 'auto',
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
          mb: 8,
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
        >
          Benefits of Telehealth
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: '750px',
            mx: 'auto',
            mb: 4,
            lineHeight: 1.7,
          }}
        >
          Telehealth gives you more flexibility while keeping your care personal,
          private, and supportive.
        </Typography>

        <Grid container spacing={2} justifyContent="center" alignItems="stretch">
          {benefits.map((benefit) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={benefit.title}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Card
                sx={{
                  width: '100%',
                  maxWidth: 340,
                  borderRadius: 3,
                  boxShadow: 'none',
                  backgroundColor: 'rgba(255,255,255,0.75)',
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {benefit.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.7, color: 'text.secondary' }}
                  >
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* How It Works */}
      <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            mb: 2,
          }}
        >
          How Telehealth Works
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: '750px',
            mx: 'auto',
            mb: 5,
            lineHeight: 1.8,
            color: 'text.secondary',
          }}
        >
          We make virtual care easy to access so you can focus on getting the
          support you need.
        </Typography>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Convenient Virtual Appointments
            </Typography>

            <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
              Telehealth appointments allow you to connect with your provider
              through a secure online visit. This can help reduce travel time,
              waiting room stress, and scheduling barriers.
              <br />
              <br />
              Virtual visits may be used for psychiatric care, medication
              management, follow-up appointments, treatment planning, and ongoing
              mental health support when appropriate.
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Support That Fits Your Life
            </Typography>

            <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
              We understand that life can be busy and unpredictable. Telehealth
              gives you a flexible way to stay connected with your provider
              while still receiving thoughtful, personalized care.
              <br />
              <br />
              If you are unsure whether telehealth is the right option for you,
              our team can help guide you through your choices.
            </Typography>
          </CardContent>
        </Card>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Ready to schedule a telehealth appointment?
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                            component="a"
                            href={zocdocLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{
                              backgroundColor: 'transparent',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17 !important',
                              fontWeight: 600,
                              px: 4,
                              py: 1.3,
                              borderRadius: '30px',
                              textTransform: 'none',
                              '&:visited': {
                                color: '#3a4c17',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(58,76,23,0.05)',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17',
                              },
                            }}
                          >
                            Schedule an Appointment
                          </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>
);

export default Telehealth;